import type { PriceId } from '@iheartjane/types';

import type {
  Flavor,
  LabResult,
  LabResultWithTerpene,
} from '@jane/bloom/types/schemas/marketplace/terpenes';
import { TerpeneEnum } from '@jane/bloom/types/schemas/marketplace/terpenes';

import { TERPENE_FLAVORS } from './constants';

export const getTerpenesFromLabResults = (
  labResults: LabResult[],
  weight: PriceId
) => {
  const labResult = labResults?.find(({ price_id }) => price_id === weight);

  if (!labResult) {
    return [];
  }

  return labResult.lab_results
    .filter(({ value }) => (value || 0) > 0)
    .reduce(
      (
        prev: LabResultWithTerpene[],
        curr: LabResult['lab_results'][number]
      ) => {
        const compoundName = curr.compound_name.toLowerCase();

        const matchingTerpene = TerpeneEnum.options.find((terpene) =>
          compoundName.endsWith(terpene)
        );

        if (!matchingTerpene) {
          return prev;
        }

        return [...prev, { ...curr, matchingTerpene }];
      },
      []
    );
};

export const getTopFlavors = (
  labResults: LabResult[] | null,
  weight: PriceId
) => {
  if (!labResults) {
    return [];
  }

  const flavors: { [flavor: string]: number } = {};
  const terpenes = getTerpenesFromLabResults(labResults, weight);

  terpenes.forEach((terpene: LabResultWithTerpene) => {
    const flavor = TERPENE_FLAVORS[terpene.matchingTerpene];

    if (flavor) {
      flavors[flavor] = (flavors[flavor] || 0) + (terpene.value || 0);
    }
  });

  return Object.entries(flavors)
    .sort(([, valueA], [, valueB]) => valueB - valueA)
    .slice(0, 3)
    .map(([key, value]) => ({ count: value, value: key as Flavor }));
};

export const getSortedTerpenes = (
  labResults: LabResult[] | null,
  weight: PriceId
) => {
  if (!labResults) {
    return [];
  }

  return getTerpenesFromLabResults(labResults, weight).sort(
    ({ value: valueA }, { value: valueB }) => valueB - valueA
  );
};
